/* AI Workstation pages (product template and landing page): gallery, sticky bars, scroll animations. No dependencies.
   Any element with data-aiws-reveal fades up when it scrolls into view. */
(function () {
  'use strict';
  var doc = document;
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var design = !!(window.Shopify && window.Shopify.designMode);
  var hasIO = 'IntersectionObserver' in window;
  var ease = 'cubic-bezier(.2,.7,.2,1)';

  function all(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function one(sel, ctx) { return (ctx || doc).querySelector(sel); }

  /* Gallery: thumbnails swap the main image with a short crossfade */
  function initGallery() {
    var stage = one('[data-aiws-stage]');
    var main = stage ? stage.querySelector('img') : null;
    var thumbs = all('[data-aiws-thumb]');
    thumbs.forEach(function (btn) {
      if (btn.__aiws) return;
      btn.__aiws = true;
      btn.addEventListener('click', function () {
        if (!main) return;
        var src = btn.getAttribute('data-aiws-thumb');
        thumbs.forEach(function (t) { t.setAttribute('aria-pressed', t === btn ? 'true' : 'false'); });
        if (main.getAttribute('src') === src) return;
        var swap = function () {
          main.removeAttribute('srcset');
          main.src = src;
          main.alt = btn.getAttribute('data-alt') || '';
          requestAnimationFrame(function () { main.classList.remove('is-swapping'); });
        };
        main.classList.add('is-swapping');
        var pre = new Image();
        pre.src = src;
        var ready = pre.decode ? pre.decode() : Promise.resolve();
        ready.then(function () { setTimeout(swap, reduce ? 0 : 160); }, swap);
      });
    });
  }

  /* Phone buy bar: shows once the main Add to cart button scrolls away, hides near the closing call to action and footer */
  function initMobileBar() {
    var bar = one('[data-aiws-mbar]');
    var buy = one('#aiws-form button[type="submit"]');
    if (!bar || !buy || !hasIO || bar.__aiws) return;
    bar.__aiws = true;
    var past = false;
    var inZone = new Map();
    var update = function () {
      var blocked = false;
      inZone.forEach(function (v) { if (v) blocked = true; });
      var on = past && !blocked;
      bar.classList.toggle('is-on', on);
      doc.body.classList.toggle('aiws-mbar-on', on);
    };
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { past = !e.isIntersecting && e.boundingClientRect.top < 0; });
      update();
    }).observe(buy);
    var zones = all('.aiws-cta, .shopify-section-group-footer-group');
    if (zones.length) {
      var zio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { inZone.set(e.target, e.isIntersecting); });
        update();
      });
      zones.forEach(function (z) { zio.observe(z); });
    }
  }

  /* Section bar: sits under the theme header when it slides in, shadows when pinned, highlights the section in view */
  function initBar() {
    var bar = one('[data-aiws-bar]');
    if (!bar || bar.__aiws) return;
    bar.__aiws = true;
    var nav = bar.querySelector('nav');
    var links = nav ? all('a[href^="#"]', nav) : [];
    links = links.filter(function (a) {
      var t = doc.getElementById(a.getAttribute('href').slice(1));
      if (!t || !t.getClientRects().length) { a.parentNode.removeChild(a); return false; }
      return true;
    });

    var header = one('.section-header');
    var syncTop = function () {
      var top = 0;
      if (header) {
        var pos = getComputedStyle(header).position;
        if ((pos === 'sticky' || pos === 'fixed') && !header.classList.contains('shopify-section-header-hidden')) {
          top = Math.round(header.getBoundingClientRect().height);
        }
      }
      bar.style.setProperty('--aiws-bar-top', top + 'px');
    };
    syncTop();
    if (header && window.MutationObserver) new MutationObserver(syncTop).observe(header, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', syncTop);

    var targets = links.map(function (a) { return { a: a, el: doc.getElementById(a.getAttribute('href').slice(1)) }; });
    var active = null;
    var setActive = function (a) {
      if (a === active) return;
      if (active) { active.classList.remove('is-active'); active.removeAttribute('aria-current'); }
      active = a;
      if (!a) return;
      a.classList.add('is-active');
      a.setAttribute('aria-current', 'true');
      if (nav.scrollWidth > nav.clientWidth + 2) {
        var nr = nav.getBoundingClientRect();
        var ar = a.getBoundingClientRect();
        var left = nav.scrollLeft + (ar.left - nr.left) - (nr.width - ar.width) / 2;
        nav.scrollTo({ left: Math.max(0, left), behavior: reduce ? 'auto' : 'smooth' });
      }
    };

    var ticking = false;
    var update = function () {
      ticking = false;
      var top = parseFloat(bar.style.getPropertyValue('--aiws-bar-top')) || 0;
      bar.classList.toggle('is-stuck', bar.getBoundingClientRect().top <= top + 1);
      var line = (window.innerHeight || 800) * 0.4;
      var current = null;
      targets.forEach(function (t) { if (t.el.getBoundingClientRect().top <= line) current = t.a; });
      setActive(current);
    };
    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  /* What's inside: hovering a part lights up its pin on the drawing, and the other way round */
  function initPins() {
    all('.aiws-inside').forEach(function (box) {
      if (box.__aiws) return;
      box.__aiws = true;
      var items = all('[data-pin]', box);
      var set = function (n, on) {
        items.forEach(function (x) { if (x.getAttribute('data-pin') === n) x.classList.toggle('is-hot', on); });
      };
      items.forEach(function (x) {
        var n = x.getAttribute('data-pin');
        x.addEventListener('mouseenter', function () { set(n, true); });
        x.addEventListener('mouseleave', function () { set(n, false); });
      });
    });
  }

  /* FAQ: smooth open and close */
  function initFaq() {
    all('.aiws-faq details').forEach(function (d) {
      if (d.__aiws) return;
      d.__aiws = true;
      var sum = d.querySelector('summary');
      var body = d.querySelector('.aiws-faq__a');
      if (!sum || !body) return;
      var busy = false;
      sum.addEventListener('click', function (ev) {
        if (reduce || !body.animate) return;
        ev.preventDefault();
        if (busy) return;
        busy = true;
        if (d.open) {
          d.classList.add('is-closing');
          var close = body.animate([{ height: body.offsetHeight + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 260, easing: ease, fill: 'forwards' });
          close.onfinish = function () { d.open = false; d.classList.remove('is-closing'); close.cancel(); busy = false; };
        } else {
          d.open = true;
          var open = body.animate([{ height: '0px', opacity: 0 }, { height: body.offsetHeight + 'px', opacity: 1 }], { duration: 340, easing: ease });
          open.onfinish = function () { busy = false; };
        }
      });
    });
  }

  /* Compare table: shadow on the pinned first column once scrolled, and start on the build being viewed */
  function initTables() {
    all('.aiws-table-wrap').forEach(function (w) {
      if (w.__aiws) return;
      w.__aiws = true;
      var update = function () { w.classList.toggle('is-scrolled', w.scrollLeft > 2); };
      w.addEventListener('scroll', update, { passive: true });
      var cur = w.querySelector('thead .is-current');
      var first = w.querySelector('tbody th');
      if (cur && w.scrollWidth > w.clientWidth + 2) {
        var off = cur.offsetLeft - (first ? first.offsetWidth : 0) - 8;
        if (off > 0) w.scrollLeft = off;
      }
      update();
    });
  }

  /* Number count-up for the stat tiles and benchmarks */
  function prepCount(n) {
    var text = n.textContent.trim();
    var m = text.match(/^([\d.,]+)(.*)$/);
    if (!m) return;
    var c = { text: text, num: parseFloat(m[1].replace(/,/g, '')), suffix: m[2], comma: m[1].indexOf(',') > -1, dec: (m[1].split('.')[1] || '').length };
    if (isNaN(c.num)) return;
    n.__aiwsCount = c;
    n.textContent = (0).toFixed(c.dec) + c.suffix;
  }
  function runCount(n) {
    var c = n.__aiwsCount;
    if (!c) return;
    n.__aiwsCount = null;
    var start = null;
    var dur = 1300;
    var fmt = function (v) {
      var s = c.comma ? v.toLocaleString('en-US', { minimumFractionDigits: c.dec, maximumFractionDigits: c.dec }) : v.toFixed(c.dec);
      return s + c.suffix;
    };
    var step = function (t) {
      if (start === null) start = t;
      var p = Math.min((t - start) / dur, 1);
      var e = 1 - Math.pow(1 - p, 4);
      n.textContent = p < 1 ? fmt(c.num * e) : c.text;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /* Section links such as #shopify-section-template--123__ai_contact_form_x carry a number that changes when the
     theme is duplicated. If that exact id is missing, land on the section with the same name instead. */
  function sectionFor(hash) {
    var m = (hash || '').match(/^#shopify-section-(?:template|sections)--\d+__([\w-]+)$/);
    if (!m || doc.getElementById(hash.slice(1))) return null;
    return one('[id^="shopify-section-"][id$="__' + m[1] + '"]');
  }
  function jumpTo(el, smooth) {
    var top = el.getBoundingClientRect().top + (window.pageYOffset || 0);
    window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: smooth && !reduce ? 'smooth' : 'auto' });
  }
  function initAnchors() {
    if (doc.__aiwsAnchors) return;
    doc.__aiwsAnchors = true;
    var fromHash = function (smooth) {
      var el = sectionFor(window.location.hash);
      if (el) jumpTo(el, smooth);
    };
    if (doc.readyState === 'complete') fromHash(false);
    else window.addEventListener('load', function () { fromHash(false); });
    window.addEventListener('hashchange', function () { fromHash(true); });
    doc.addEventListener('click', function (ev) {
      var a = ev.target.closest && ev.target.closest('a[href*="#shopify-section-"]');
      if (!a || ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      if (a.hostname !== window.location.hostname || a.pathname !== window.location.pathname) return;
      var el = sectionFor(a.hash);
      if (!el) return;
      ev.preventDefault();
      if (window.location.hash !== a.hash && window.history.pushState) window.history.pushState(null, '', a.hash);
      jumpTo(el, true);
    });
  }

  /* Scroll reveals. Only content below the fold is animated, so nothing visible on load ever blinks. */
  var SEL = '[data-aiws-reveal], .aiws-head, .aiws-overview > div:first-child, .aiws-stat, .aiws-inside__art, .aiws-parts li, .aiws-card, .aiws-legend, .aiws-fit__row, .aiws-bench, .aiws-cost, .aiws-payback, .aiws-table-wrap, .aiws-mini, .aiws-specs__head, .aiws-group, .aiws-faq__intro, .aiws-faq details, .aiws-cta__box';
  var revealIO = null;
  function show(el) {
    el.classList.add('is-in', 'aiws-seen');
    all('[data-aiws-count]', el).forEach(runCount);
    var i = parseFloat(el.style.getPropertyValue('--i')) || 0;
    setTimeout(function () { el.classList.remove('aiws-rv', 'is-in'); }, 950 + i * 70);
  }
  function initReveal(scope) {
    if (!hasIO || reduce || design) return;
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          revealIO.unobserve(e.target);
          show(e.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    }
    var vh = window.innerHeight || 800;
    all(SEL, scope).forEach(function (el) {
      if (el.__aiwsRv || el.closest('.aiws--hero')) return;
      el.__aiwsRv = true;
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) return;
      var sibs = Array.prototype.filter.call(el.parentNode.children, function (c) { return c.matches(SEL); });
      el.style.setProperty('--i', Math.min(Math.max(sibs.indexOf(el), 0), 8));
      all('[data-aiws-count]', el).forEach(prepCount);
      el.classList.add('aiws-rv');
      revealIO.observe(el);
    });
  }

  function init() {
    initGallery();
    initMobileBar();
    initBar();
    initPins();
    initFaq();
    initTables();
    initAnchors();
    initReveal(doc);
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();
  doc.addEventListener('shopify:section:load', function () {
    initGallery();
    initMobileBar();
    initBar();
    initPins();
    initFaq();
    initTables();
  });
})();
